import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import type { BankAccount } from '@api/btlApplicationForm/types';
import { Button } from '@components/common/Button';
import { AccountDetails } from './AccountDetails';
import { BankAddress } from './BankAddress';
import { BankDetailsSectionsLayout } from '@pages/BankDetailsPage/components/layout/BankDetailsSectionsLayout';
import { buildBankAddressPayload, defaultBankDetailsFormValues, mapBankAccountToFormValues } from './helpers';
import type { BankDetailsFormValues } from './types';

export interface IBankDetailsFormProps {
  applicationId: string;
  bankAccount?: BankAccount;
  readOnly?: boolean;
  onCancel?: () => void;
  onSaved?: () => void;
}

export function BankDetailsForm({
  applicationId,
  bankAccount,
  readOnly = false,
  onCancel,
  onSaved,
}: IBankDetailsFormProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm<BankDetailsFormValues>({
    defaultValues: bankAccount ? mapBankAccountToFormValues(bankAccount) : defaultBankDetailsFormValues,
  });

  useEffect(() => {
    if (!bankAccount) {
      return;
    }

    form.reset(mapBankAccountToFormValues(bankAccount));
  }, [bankAccount, form]);

  const { mutate: updateBankAccount, isLoading: isSaving } = useMutation({
    mutationFn: (values: BankDetailsFormValues) =>
      btlApplicationFormApi.updateBankAccount(applicationId, {
        sortCode: values.accountDetails.sortCode,
        accountNumber: values.accountDetails.accountNumber,
        accountName: values.accountDetails.accountName,
        bankName: values.accountDetails.bankName,
        bankAddress: buildBankAddressPayload(values.bankAddress),
      }),
    onSuccess: () => {
      enqueueSnackbar(intl.formatMessage({ id: 'applicationDetails.bankDetails.saveSuccess' }), { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['btl-application-in-progress', applicationId] });
      onSaved?.();
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || intl.formatMessage({ id: 'applicationDetails.bankDetails.saveError' }), {
        variant: 'error',
      });
    },
  });

  const submitFormHandler = (values: BankDetailsFormValues) => {
    if (!values.bankAddress.isManual && !values.bankAddress.selectedAddress) {
      form.setError('bankAddress.selectedAddress', {
        type: 'manual',
        message: intl.formatMessage({ id: 'common.error.requiredField' }),
      });
      return;
    }

    updateBankAccount(values);
  };

  const sections = (
    <BankDetailsSectionsLayout>
      <AccountDetails disabled={readOnly} />
      <BankAddress disabled={readOnly} />
    </BankDetailsSectionsLayout>
  );

  if (readOnly) {
    return <FormProvider {...form}>{sections}</FormProvider>;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <button
          type='button'
          className='mb-6 flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-base font-bold text-black'
          onClick={onCancel}
        >
          <ArrowLeftOutlined />
          <FormattedMessage id='applicationDetails.bankDetails.title' />
        </button>
        {sections}
        <div className='mt-6 flex gap-3'>
          <Button variant='outlined' className='rounded-lg normal-case' onClick={onCancel} disabled={isSaving}>
            <FormattedMessage id='applicationDetails.bankDetails.cancel' />
          </Button>
          <Button
            type='submit'
            variant='contained'
            className='rounded-lg bg-green-800 normal-case hover:bg-green-900'
            disabled={isSaving}
          >
            <FormattedMessage
              id={isSaving ? 'applicationDetails.bankDetails.saving' : 'applicationDetails.bankDetails.save'}
            />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
